import AppModal from "@components/modals/AppModal";
import {
  listAiPromptsRequest,
  updateAiPromptRequest,
} from "@services/ai/aiApi";
import "@assets/css/mode_tool/aiPromptEditModal.css";
import { useEffect, useState } from "react";

function AiPromptEditModal({ isOpen, onClose, action }) {
  const promptKey = String(action?.promptKey || "");
  const [promptText, setPromptText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    let isCurrent = true;
    setPromptText("");
    setError("");
    setIsLoading(true);

    listAiPromptsRequest()
      .then((response) => {
        if (!isCurrent) return;
        const prompts = Array.isArray(response?.body?.prompts)
          ? response.body.prompts
          : [];
        const currentPrompt = prompts.find(
          (item) => String(item?.prompt_key || "") === promptKey,
        );
        setPromptText(String(currentPrompt?.prompt_text || ""));
      })
      .catch((requestError) => {
        if (!isCurrent) return;
        setError(requestError?.message || "Could not load the saved prompt.");
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [isOpen, promptKey]);

  async function handleSave() {
    if (!promptKey || isLoading || isSaving || error) return;
    setIsSaving(true);
    setError("");
    try {
      await updateAiPromptRequest(promptKey, promptText);
      onClose?.();
    } catch (requestError) {
      setError(requestError?.message || "Could not save the prompt.");
    } finally {
      setIsSaving(false);
    }
  }

  const isBusy = isLoading || isSaving;
  const unsupportedMessage = !promptKey
    ? "The API documentation does not define a prompt key for this tool yet."
    : "";

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      closeDisabled={isSaving}
      title={action?.editTitle || action?.label || "Edit AI prompt"}
      size="sm"
      className="ai-prompt-edit-modal"
    >
      <p className="ai-prompt-edit-description">
        Edit the custom instructions for this AI tool using Markdown.
      </p>
      <label className="ai-prompt-edit-label" htmlFor="ai-prompt-markdown">
        Prompt (Markdown)
      </label>
      <textarea
        id="ai-prompt-markdown"
        className="ai-prompt-edit-textarea"
        value={promptText}
        onChange={(event) => setPromptText(event.target.value)}
        placeholder="Write the custom instructions in Markdown…"
        rows={12}
        disabled={isBusy || Boolean(unsupportedMessage)}
        spellCheck="false"
      />
      {isLoading ? <p className="ai-prompt-edit-status">Loading saved prompt…</p> : null}
      {unsupportedMessage ? (
        <p className="ai-prompt-edit-status" role="status">{unsupportedMessage}</p>
      ) : null}
      {error ? <p className="ai-prompt-edit-error" role="alert">{error}</p> : null}
      <div className="tour-add-editor-actions ai-prompt-edit-actions">
        <button type="button" className="button-ghost" onClick={onClose} disabled={isSaving}>
          Cancel
        </button>
        <button
          type="button"
          className="button-primary"
          onClick={handleSave}
          disabled={isBusy || Boolean(error) || !promptKey}
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </AppModal>
  );
}

export default AiPromptEditModal;
